/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-29 07:46:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Layout,
  Card,
  Icon,
  DataTable,
  TextField,
  Link,
  Pagination,
  SkeletonDisplayText,
  TextStyle
} from '@shopify/polaris';
import { SearchMinor, LockMinor, ExternalSmallMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { URL } from 'src/constants/links';
import DidYouKnow from 'src/components/views/redirect/did-you-know';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';
import ActionsGroupOnListing from 'src/components/views/redirect/actions-group-on-listing';
import NoRedirects, { NotFoundRedirects } from 'src/components/views/redirect/no-redirects';
import TableHeadingCellWithTooltip from 'src/components/views/redirect/table-heading-cell-with-tooltip';
import ImportModal from './import';
import ExportModal from './export';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import TitleHelp from 'src/components/views/common/title-help';
import MainMenu from 'src/components/containers/main-menu';

import { NUMBER_REDIRECT_PER_PAGE } from 'src/constants/redirect';
import { prettyText } from 'src/helpers';
import { buildRedirects } from 'src/helpers/redirect-helpers';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import * as redirectActions from 'src/stores/actions/redirect-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';

import LockImage from 'src/assets/images/lock-unlock-image/redirect-lock.svg';

type Props = {
  redirects: Array<Object>,
  total: number,
  loading: boolean,
  success: boolean,
  successFlag: number,
  getRedirects: () => void,
  updateRedirect: () => void,
  getChecklist: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeImportModal: boolean,
  activeExportModal: boolean,
  inputSearchText: string,
  skip: number,
  limit: number,
  firstPageLoading: boolean,
  hasPrevious: boolean,
  hasNext: boolean,
  searchEnabled: Object,
  youtubeModal: boolean,
  changePlanFromReviewIOModal: boolean
};

const renderRowsLoadingFilter = (count: number) => {
  let rows = [];
  for (var i = 0; i < count; i++) {
    rows.push([
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />
    ]);
  }

  return rows;
};

class Redirect extends Component<ComposedProps, State> {
  timerTyping = undefined;

  state = {
    // Import modal
    activeImportModal: false,

    // Export modal
    activeExportModal: false,

    // Search string
    inputSearchText: '',

    skip: 0,
    limit: NUMBER_REDIRECT_PER_PAGE,
    firstPageLoading: true,
    hasPrevious: false,
    hasNext: true,

    // Control search enable for redirects
    searchEnabled: {},

    youtubeModal: false,
    changePlanFromReviewIOModal: false
  };

  componentDidMount() {
    // List filter tree for first page, get 12 filter trees: 1 default filter, 1 search filter, 10 custom filter
    const { skip, limit } = this.state;
    this.props.getRedirects('', skip, limit);
  }

  componentWillReceiveProps(nextProps) {
    const { skip, limit } = this.state;
    const { total, success, redirects, successFlag } = nextProps;
    const { successFlag: successFlagPrev } = this.props;

    const hasNext = skip + limit < total;
    const hasPrevious = skip > 0;
    let updatedState = { hasNext, hasPrevious };

    if (success === true && successFlag !== successFlagPrev) {
      const searchEnabled = this.collectSearchEnabled(redirects);
      updatedState = _.assign(updatedState, { searchEnabled });
    }

    this.setState(updatedState);
  }

  collectSearchEnabled = redirects => {
    const status = {};
    redirects.forEach(redirect => {
      const { id, enabled } = redirect;
      status[id] = enabled;
    });

    return status;
  };

  render() {
    const { i18n, loading, redirects, withMenu, enabledFeature } = this.props;
    const { skip, limit, inputSearchText, hasNext, hasPrevious, youtubeModal } = this.state;

    // Render redirects on table
    let rowRedirects;
    if (!loading) {
      rowRedirects = this.buildRows();
    } else {
      const numberRender = _.isEmpty(redirects) ? 5 : redirects.length;
      rowRedirects = renderRowsLoadingFilter(numberRender);
    }

    // Check having redirect
    const havingRedirects = !_.isEmpty(redirects);

    return (
      <div className="BC-Page BC-Page__SuggestionDictionary BC-Page__Redirect">
        {withMenu && <MainMenu menuId="menu-search-redirect" />}
        <Page
          title={i18n.translate('AdminHTML.Redirects.Redirects.title')}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <ActionsGroupOnListing
                q={inputSearchText}
                skip={skip}
                limit={limit}
                havingRedirects={havingRedirects}
              />
            </div>
          }>
          <TitleHelp
            i18n={i18n}
            onPlay={this.handleOpenYoutubeModal}
            hrefUserManual={URL['Link_V2_Search_Redirects_ManualGuide']}
            onAnalytics={
              enabledFeature ? () => history.push(routes.analytics.overviewSearch()) : null
            }
            onImport={enabledFeature ? this.handleChangeImportModal : null}
            onExport={enabledFeature ? this.handleChangeExportModal : null}
          />

          {!enabledFeature ? (
            this.renderLockFeature(i18n)
          ) : (
            <Layout>
              <Layout.Section>
                <div className="BC-Container__Notifications">
                  {/* Did you know? */}
                  <DidYouKnow />
                </div>
              </Layout.Section>
              {/* List of redirect */}
              <Layout.Section>
                <Card>
                  <Card.Section>
                    <div className="BC__Search-header">
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.Redirects.Redirects.tableTitle')}
                      </TextStyle>
                      <div
                        className="search"
                        onKeyDown={this.handleKeyDown}
                        onKeyUp={this.handleKeyUp}>
                        <TextField
                          onChange={this.onSearchRedirects}
                          value={this.state.inputSearchText}
                          prefix={<Icon source={SearchMinor} color="base" />}
                          placeholder={i18n.translate(
                            'AdminHTML.Redirects.Redirects.inputSearchText'
                          )}
                        />
                      </div>
                    </div>
                  </Card.Section>
                  <Card.Section>
                    <div className={havingRedirects ? '' : 'noRedirects'}>
                      <DataTable
                        verticalAlign="middle"
                        columnContentTypes={['text', 'text', 'text']}
                        headings={[
                          i18n.translate('AdminHTML.Redirects.Redirects.status'),
                          <TableHeadingCellWithTooltip
                            heading={i18n.translate('AdminHTML.Redirects.Redirects.searchTerm')}
                            tooltipContent={i18n.translate(
                              'AdminHTML.Redirects.Redirects.searchTerm_Tooltip'
                            )}
                          />,
                          i18n.translate('AdminHTML.Redirects.Redirects.redirectTo')
                        ]}
                        rows={rowRedirects}
                      />
                      {_.isEmpty(redirects) &&
                        !loading &&
                        (this.state.inputSearchText ? (
                          <NotFoundRedirects i18n={i18n} />
                        ) : (
                          <NoRedirects />
                        ))}
                    </div>
                  </Card.Section>
                </Card>

                {havingRedirects ? (
                  <div className="pagination">
                    {(hasPrevious || hasNext) && (
                      <Pagination
                        hasPrevious={hasPrevious}
                        onPrevious={this.getPreviousPage}
                        hasNext={hasNext}
                        onNext={this.getNextPage}
                      />
                    )}
                    <div className="actionsGroup">
                      <ActionsGroupOnListing
                        q={inputSearchText}
                        skip={skip}
                        limit={limit}
                        havingRedirects={havingRedirects}
                      />
                    </div>
                  </div>
                ) : (
                  ''
                )}
              </Layout.Section>
            </Layout>
          )}
        </Page>

        <ImportModal
          active={this.state.activeImportModal}
          handleChange={this.handleChangeImportModal}
          getRedirectsFirstPage={this.getRedirectsFirstPage}
          history={history}
        />

        <ExportModal
          active={this.state.activeExportModal}
          handleChange={this.handleChangeExportModal}
        />

        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Search_Redirects_Video']}
          title={i18n.translate('AdminHTML.Redirects.Redirects.youtubeModal_Title')}
          onClose={this.handleCloseYoutubeModal}
        />
        <ChangePlanFromReviewIOModal
          open={this.state.changePlanFromReviewIOModal}
          onClose={() => this.setState({ changePlanFromReviewIOModal: false })}
        />
      </div>
    );
  }

  renderLockFeature = i18n => {
    const { useBoltOn } = this.props;
    return (
      <div className="pfs-feature-lock-card">
        <img src={LockImage} alt="in-collection-search-lock" />
        <div className="content">
          {i18n.translate(`AdminHTML.Redirects.Plan.lockCard_Content01`)}
        </div>
        <p>{i18n.translate(`AdminHTML.Redirects.Plan.lockCard_Content02`)}</p>
        <div className="action">
          <div
            className={`Polaris-Button`}
            onClick={() =>
              useBoltOn
                ? this.setState({ changePlanFromReviewIOModal: true })
                : history.push(routes.subscription.package())
            }>
            <Icon source={LockMinor} />
            {i18n.translate(`AdminHTML.Common.Action.upgradeToUnlock`)}
          </div>
          <Link external url={URL['Link_V2_Search_Redirects_ManualGuide']}>
            {i18n.translate('AdminHTML.Common.Action.learnMore')}
            <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
          </Link>
        </div>
      </div>
    );
  };

  buildRows = () => {
    const { redirects } = this.props;
    let rows = [];
    if (!_.isEmpty(redirects)) {
      _.forEach(redirects, redirect => {
        const { id, terms, redirectTo } = redirect;

        rows.push([
          this.renderCheckbox(id),
          <span>{terms} </span>,
          <span style={{ color: 'var(--p-interactive)' }}>{redirectTo}</span>
        ]);
      });
    }

    return rows;
  };

  // Render search enable box
  renderCheckbox = id => {
    const { searchEnabled } = this.state;
    return (
      <label className="BC-Checkbox-Status">
        <input
          type="checkbox"
          checked={searchEnabled[id] || false}
          onChange={() => this.handleChangeStatus(id)}
        />
        <span />
      </label>
    );
  };

  // Handle key press event on search field
  // handleKeyPress = event => {
  //   const enterKeyPressed = event.keyCode === 13;
  //   if (enterKeyPressed) {
  //     event.preventDefault();
  //     this.handleSearch();
  //   }
  // };

  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    const charList = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const key = event.key.toLowerCase();
    const keyCode = event.keyCode;

    if (charList.indexOf(key) === -1 && keyCode !== 8) {
      return;
    }

    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.handleSearch();
    }, 500);
  };

  // On search text change
  onSearchRedirects = inputSearchText => {
    this.setState({ inputSearchText });
  };

  // Search redirects
  handleSearch = () => {
    let inputSearchText = this.state.inputSearchText.toLowerCase().trim();
    inputSearchText = prettyText(inputSearchText);

    // Having search text, search custom filter for page 1
    if (inputSearchText) {
      // Set limit per page
      this.setState({ skip: 0, limit: NUMBER_REDIRECT_PER_PAGE });

      this.props.getRedirects(inputSearchText, 0, NUMBER_REDIRECT_PER_PAGE);
    } else {
      // Search text is empty, get all filter tree for page 1
      const skip = 0,
        limit = NUMBER_REDIRECT_PER_PAGE;

      // Set skip, limit for page 1
      this.setState({ skip, limit });

      // Get filter trees
      this.props.getRedirects('', skip, limit);
    }
  };

  // Enable/disable redirect term
  handleChangeStatus = async id => {
    const { searchEnabled } = this.state;
    const { redirects, updateRedirect, getChecklist } = this.props;

    // Update status
    const newStatus = !searchEnabled[id];
    searchEnabled[id] = newStatus;

    // Update redirect
    let redirect = _.find(redirects, { id });
    redirect.enabled = newStatus;

    // Build redirects data before saving
    const updatedRedirects = buildRedirects([redirect]);
    await updateRedirect(updatedRedirects, 'changeStatus');
    await getChecklist();
    this.setState({ searchEnabled });
  };

  // Go to previous page
  getPreviousPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const previousSkip = skip - limit;
    const previousLimit = NUMBER_REDIRECT_PER_PAGE;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Set skip, limit for next page
    this.setState({ skip: previousSkip, limit: previousLimit });
    this.props.getRedirects(inputSearchText || '', previousSkip, previousLimit);
  };

  // Go to next page
  getNextPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const { total } = this.props;
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Set skip, limit for next page and disable flag for first page loading, don't need to display skeleton loading
    this.setState({ skip: nextSkip, limit });
    this.props.getRedirects(inputSearchText || '', nextSkip, NUMBER_REDIRECT_PER_PAGE);
  };

  // Get redirect for first page
  getRedirectsFirstPage = () => {
    this.setState({ inputSearchText: '', skip: 0, limit: NUMBER_REDIRECT_PER_PAGE });
    this.props.getRedirects('', 0, NUMBER_REDIRECT_PER_PAGE);
  };

  // Hide/show import modal
  handleChangeImportModal = () => {
    this.setState(({ activeImportModal }) => ({
      activeImportModal: !activeImportModal
    }));
  };

  // Hide/show export modal
  handleChangeExportModal = () => {
    this.setState(({ activeExportModal }) => ({
      activeExportModal: !activeExportModal
    }));
  };

  handleOpenYoutubeModal = () => this.setState({ youtubeModal: true });
  handleCloseYoutubeModal = () => this.setState({ youtubeModal: false });
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getRedirects: (q, skip, limit) =>
    dispatch(redirectActions.getRedirects(q, skip, limit, ownProps.i18n)),
  updateRedirect: (redirect, actionName) =>
    dispatch(redirectActions.updateRedirect(redirect, actionName, ownProps.i18n)),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    enabledFeature: _.get(state.oauth.tenantApp, 'settings.search.settings.redirects', true),
    redirects: _.get(state.redirect, 'redirects', []),
    total: _.get(state.redirect, 'total', 0),
    loading: _.get(state.redirect, 'loading', false),
    success: _.get(state.redirect, 'success', false),
    successFlag: _.get(state.redirect, 'successFlag', 0)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Redirect));
